// Loads Edugen's Google rating + reviews with the current Places API
// (`google.maps.places.Place`). The legacy google.maps.places.PlacesService
// is closed to new customers since March 2025 and logs a deprecation
// warning. Requires "Places API (New)" to be enabled for
// REACT_APP_GOOGLE_MAPS_API_KEY in Google Cloud.

const GOOGLE_BUSINESS_QUERY = 'Edugen Study Abroad Consultants, C-707, Shiksha Bharti School Rd, near Ramphal Chowk Road, Block C, Sector 7 Dwarka, Delhi 110075';
const GOOGLE_PLACE_ID_CACHE_KEY = 'edugen_google_place_id';

const loadGoogleMapsScript = () => new Promise((resolve, reject) => {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY'));
    return;
  }
  if (window.google?.maps?.importLibrary) {
    resolve(window.google);
    return;
  }
  const existing = document.querySelector('script[data-map="google-places"]');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.google));
    existing.addEventListener('error', (e) => reject(e));
    return;
  }
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.dataset.map = 'google-places';
  script.onload = () => resolve(window.google);
  script.onerror = (e) => reject(e);
  document.head.appendChild(script);
});

const readCachedPlaceId = () => {
  try { return localStorage.getItem(GOOGLE_PLACE_ID_CACHE_KEY); } catch (_) { return null; }
};

const findPlaceId = async (Place) => {
  const cached = readCachedPlaceId();
  if (cached) return cached;
  const { places } = await Place.searchByText({
    textQuery: GOOGLE_BUSINESS_QUERY,
    fields: ['id'],
    maxResultCount: 1,
  });
  const placeId = places?.[0]?.id;
  if (!placeId) throw new Error('Place.searchByText returned no results');
  try { localStorage.setItem(GOOGLE_PLACE_ID_CACHE_KEY, placeId); } catch (_) {}
  return placeId;
};

const fetchPlaceDetails = async (Place, placeId) => {
  const place = new Place({ id: placeId });
  await place.fetchFields({ fields: ['rating', 'userRatingCount', 'reviews', 'googleMapsURI'] });
  return place;
};

const formatReviewDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * @returns {Promise<{ reviews: Array<{img, name, rating, date, text}>, rating: number|null, total: number|null, mapsUrl: string|null }>}
 */
export async function loadGoogleReviews() {
  const google = await loadGoogleMapsScript();
  const { Place } = await google.maps.importLibrary('places');

  let place;
  try {
    place = await fetchPlaceDetails(Place, await findPlaceId(Place));
  } catch (firstError) {
    // A cached place id can go stale; drop it and look the business up again once.
    if (!readCachedPlaceId()) throw firstError;
    try { localStorage.removeItem(GOOGLE_PLACE_ID_CACHE_KEY); } catch (_) {}
    place = await fetchPlaceDetails(Place, await findPlaceId(Place));
  }

  return {
    reviews: (place.reviews || []).map((rv) => ({
      img: rv.authorAttribution?.photoURI || '',
      name: rv.authorAttribution?.displayName || 'Google User',
      rating: rv.rating || 0,
      date: formatReviewDate(rv.publishTime),
      text: rv.text || '',
    })),
    rating: place.rating ?? null,
    total: place.userRatingCount ?? null,
    mapsUrl: place.googleMapsURI || null,
  };
}
